import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MoreHorizontal, Edit2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

interface WorkspaceActionsProps {
  workspace: {
    id: string;
    name: string;
    user_id: string;
    is_default?: boolean;
  };
  onWorkspaceUpdate?: (workspaceId: string, newName: string) => void;
  onWorkspaceDelete?: (workspaceId: string) => void;
  className?: string;
}

const WorkspaceActions: React.FC<WorkspaceActionsProps> = ({
  workspace,
  onWorkspaceUpdate,
  onWorkspaceDelete,
  className = ''
}) => {
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState(workspace.name);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditWorkspace = async () => {
    const trimmedName = editName.trim();
    
    // Validation
    if (!trimmedName) {
      toast.error('Workspace name cannot be empty');
      return;
    }

    if (trimmedName === workspace.name) {
      setIsEditDialogOpen(false);
      return;
    }

    if (trimmedName.length > 100) {
      toast.error('Workspace name cannot exceed 100 characters');
      return;
    }

    setIsUpdating(true);

    try {
      const { error } = await supabase
        .from('workspaces')
        .update({ 
          name: trimmedName,
          updated_at: new Date().toISOString()
        })
        .eq('id', workspace.id)
        .eq('user_id', workspace.user_id);

      if (error) throw error;

      // Call parent update handler
      onWorkspaceUpdate?.(workspace.id, trimmedName);
      setIsEditDialogOpen(false);
      
      toast.success('Workspace name updated successfully');
    } catch (error: any) {
      console.error('Error updating workspace name:', error);
      toast.error(error.message || 'Failed to update workspace name');
    } finally {
      setIsUpdating(false);
    }
  };

  const resetEditForm = () => {
    setEditName(workspace.name);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={`h-8 w-8 ${className}`}>
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open workspace actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem 
            onClick={() => {
              resetEditForm();
              setIsEditDialogOpen(true);
            }}
          >
            <Edit2 className="mr-2 h-4 w-4" />
            Edit Name
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Workspace Dialog */}
      <AlertDialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Workspace Name</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new name for your workspace. This will update the workspace name everywhere it appears.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter workspace name"
                maxLength={100}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEditWorkspace();
                  }
                }}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEditWorkspace}
              disabled={isUpdating || !editName.trim()}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Name'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WorkspaceActions;